/**
 * Tokens y piezas compartidas por los gráficos de CRM.
 * Todo el color pasa por clases semánticas de Tailwind para que dark mode
 * funcione sin colores hardcodeados en el markup.
 */

/** Ejes recesivos: la grilla nunca compite con las marcas. */
export const chartAxisProps = {
  tickLine: false,
  axisLine: { className: 'stroke-neutral-300 dark:stroke-neutral-600' },
  tick: { className: 'fill-neutral-500 dark:fill-neutral-400', fontSize: 11 },
} as const;

export const chartGridProps = {
  strokeDasharray: '3 3',
  vertical: false,
  className: 'stroke-neutral-200 dark:stroke-neutral-700',
} as const;

/** Forma del payload de recharts (ValueType admite arrays en series apiladas). */
export interface TooltipEntry<TPoint = unknown> {
  name?: string | number;
  value?: string | number | readonly (string | number)[];
  color?: string;
  payload?: TPoint;
}

export interface ChartTooltipProps<TPoint = unknown> {
  active?: boolean;
  label?: string | number;
  payload?: readonly TooltipEntry<TPoint>[];
  /** Filas extra derivadas del punto (ej. mensajes del día). */
  extra?: Array<{ name: string; value: string }>;
}

function fmtValue(value: TooltipEntry['value']): string {
  if (value === undefined) return '—';
  if (Array.isArray(value)) return value.join(' – ');
  return typeof value === 'number' ? value.toLocaleString('es-CO') : String(value);
}

/** Tooltip propio: recharts no permite estilar el default con clases. */
export function ChartTooltip({ active, label, payload, extra }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 shadow-md">
      <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
      {payload.map((entry) => (
        <p
          key={String(entry.name)}
          className="text-body-sm text-neutral-900 dark:text-neutral-100 flex items-center gap-2"
        >
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          />
          {entry.name}: <span className="font-semibold tabular-nums">{fmtValue(entry.value)}</span>
        </p>
      ))}
      {extra?.map((row) => (
        <p key={row.name} className="text-caption text-neutral-500 dark:text-neutral-400 pl-4">
          {row.name}: {row.value}
        </p>
      ))}
    </div>
  );
}

/** Placeholder para rangos sin datos — nunca un gráfico vacío. */
export function ChartEmpty({
  message = 'Sin datos en el rango seleccionado',
}: { message?: string }) {
  return (
    <div className="h-full flex items-center justify-center text-xs font-mono text-neutral-500 dark:text-neutral-400">
      {message}
    </div>
  );
}
